import React from 'react';
import { Check, Info } from 'lucide-react';
import { Button } from './ui/button';

const Services = () => {
  const whatToExpect = [
    'Professional consultation to understand your vision',
    'Custom freehand painting technique',
    'Processing time with expert monitoring',
    'Toning to neutralize brassiness and add shine',
    'Luxury wash with premium products',
    'Nourishing treatment application',
    'Signature blowout and styling',
  ];

  const comparisonData = [
    { feature: 'Application', balayage: 'Hand-painted freehand', highlights: 'Foils with uniform sections' },
    { feature: 'Result', balayage: 'Natural, dimensional gradient', highlights: 'Uniform, even color' },
    { feature: 'Grow-out', balayage: 'Soft, intentional blend', highlights: 'Harsh line of demarcation' },
    { feature: 'Maintenance', balayage: 'Every 10-16 weeks', highlights: 'Every 6-8 weeks' },
    { feature: 'Best For', balayage: 'Natural, low-maintenance look', highlights: 'All-over brightness' },
  ];

  return (
    <section id="services" className="section-padding bg-gradient-to-b from-black via-[#0a0a0a] to-black relative">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-[#d4af37] text-sm font-semibold tracking-[0.2em] uppercase">Our Services</span>
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-5xl md:text-6xl font-light text-[#f5f5f0] mb-6">
            Experience the
            <span className="block text-[#d4af37]">Balayage Difference</span>
          </h2>
        </div>

        {/* Balayage vs Highlights */}
        <div className="mb-20">
          <h3 className="font-['Cormorant_Garamond'] text-4xl text-[#f5f5f0] text-center mb-12">
            Balayage vs. Traditional Highlights
          </h3>
          <div className="bg-gradient-to-br from-[#1a1a1a] to-black border border-[#d4af37]/30 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#d4af37]/30">
                    <th className="text-left p-6 text-[#f5f5f0]/60 font-medium">Feature</th>
                    <th className="text-left p-6 text-[#d4af37] font-semibold">Balayage</th>
                    <th className="text-left p-6 text-[#f5f5f0]/60 font-medium">Traditional Highlights</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <tr key={index} className="border-b border-[#d4af37]/10 hover:bg-[#d4af37]/5 transition-colors">
                      <td className="p-6 text-[#f5f5f0]/80 font-medium">{row.feature}</td>
                      <td className="p-6 text-[#f5f5f0]">{row.balayage}</td>
                      <td className="p-6 text-[#f5f5f0]/60">{row.highlights}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* What to Expect */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="font-['Cormorant_Garamond'] text-4xl text-[#f5f5f0] mb-6">
              What to Expect at Your Session
            </h3>
            <p className="text-[#f5f5f0]/70 leading-relaxed mb-8">
              Sessions run 2.5 to 4 hours depending on your hair length, density, and desired transformation. We recommend arriving with clean, dry hair for the best color uptake.
            </p>
            <div className="space-y-4 mb-8">
              {whatToExpect.map((item, index) => (
                <div key={index} className="flex items-start space-x-4 group">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#d4af37]/20 flex items-center justify-center mt-1 group-hover:bg-[#d4af37]/30 transition-colors">
                    <Check className="text-[#d4af37]" size={14} />
                  </div>
                  <p className="text-[#f5f5f0]/80 leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
            <Button
              data-testid="services-book-cta"
              onClick={() => window.open('https://www.fresha.com/a/prep-beauty-parlour-vancouver-1305-burrard-street-hlh2s5oh', '_blank')}
              className="bg-[#d4af37] hover:bg-[#b8941f] text-black font-semibold px-8 py-6 text-lg rounded-none transition-all duration-300 hover:shadow-[0_0_40px_rgba(212,175,55,0.4)]"
            >
              Book Your Transformation
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-[#1a1a1a] to-black border border-[#d4af37]/20 p-6 hover:border-[#d4af37]/40 transition-all duration-300">
                <div className="text-4xl font-bold text-[#d4af37] mb-2">2.5-4</div>
                <div className="text-[#f5f5f0]/60 text-sm">Hours Session Time</div>
              </div>
              <div className="bg-gradient-to-br from-[#1a1a1a] to-black border border-[#d4af37]/20 p-6 hover:border-[#d4af37]/40 transition-all duration-300">
                <div className="text-4xl font-bold text-[#d4af37] mb-2">10-16</div>
                <div className="text-[#f5f5f0]/60 text-sm">Weeks Between Visits</div>
              </div>
            </div>
            <div className="space-y-6 mt-12">
              <div className="bg-gradient-to-br from-[#1a1a1a] to-black border border-[#d4af37]/20 p-6 hover:border-[#d4af37]/40 transition-all duration-300">
                <div className="text-4xl font-bold text-[#d4af37] mb-2">100%</div>
                <div className="text-[#f5f5f0]/60 text-sm">Custom Color Match</div>
              </div>
              <div className="bg-gradient-to-br from-[#1a1a1a] to-black border border-[#d4af37]/20 p-6 hover:border-[#d4af37]/40 transition-all duration-300">
                <div className="text-4xl font-bold text-[#d4af37] mb-2">400+</div>
                <div className="text-[#f5f5f0]/60 text-sm">Happy Clients</div>
              </div>
            </div>
          </div>
        </div>

        {/* Dark Hair Info */}
        <div className="mt-20 bg-gradient-to-r from-[#d4af37]/10 to-transparent border-l-4 border-[#d4af37] p-8">
          <div className="flex items-start space-x-4">
            <Info className="text-[#d4af37] flex-shrink-0 mt-1" size={24} />
            <div>
              <h4 className="font-['Cormorant_Garamond'] text-2xl text-[#f5f5f0] mb-3">Balayage on Dark Hair</h4>
              <p className="text-[#f5f5f0]/70 leading-relaxed">
                Yes — balayage works beautifully on dark hair! While it may require a pre-lightening step to achieve lighter tones, dark-to-light balayage is one of the most dramatic and popular transformations. Our experienced colourists can achieve stunning results while maintaining your hair's health and integrity.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
